import {
  CircularProgress,
  Drawer,
  Grid,
  IconButton,
  Paper,
} from '@mui/material';
import { subDays, subHours, subMonths, subWeeks } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import WeatherChart from '../weatherWalls/WeatherChart';
import CurrentConditions from '../weather/CurrentConditions';
import { ChevronRight, ChevronLeft } from '@mui/icons-material';
import InfoPopup from '../../components/InfoPopup';
import WeatherDetailsFilters from './WeatherDetailsFilters';
import { useFormatForecastData } from 'features/forecasts/hooks/useFormatForecastData';
import { useLocation } from 'react-router-dom';
import { useWeatherWindow } from 'features/weatherWalls/api/getWeatherWindow';
import { useLocationHistory } from 'features/locations/api/getLocationHistory';
import { WeatherDetailsForm } from './types';
import { useForm } from 'react-hook-form';
import { zonedTimeToUtc } from 'date-fns-tz';
import { useUserDateTime } from '../timezone/hooks/useUserDateTime';

const WeatherDetailsView = () => {
  const drawerContainerRef = useRef<HTMLDivElement | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(true);
  const [drawerHeight, setDrawerHeight] = useState<number>(0);
  const pathName = useLocation().pathname;
  const splitPath = pathName.split('/');
  const windowId = Number(splitPath[splitPath.length - 1]);
  const { timezone } = useUserDateTime();
  const { data: thisWindow } = useWeatherWindow({
    windowId,
  });
  // Compute initial state for form and chart
  const startDateForThisWindow = useMemo(() => {
    const now = new Date();
    switch (thisWindow?.range_unit) {
      case 'hours':
        return subHours(now, thisWindow.range);
      case 'days':
        return subDays(now, thisWindow.range);
      case 'weeks':
        return subWeeks(now, thisWindow.range);
      case 'months':
        return subMonths(now, thisWindow.range);
      default:
        return subDays(now, 1);
    }
  }, [thisWindow?.range, thisWindow?.range_unit]);

  const {
    control,
    setValue,
    reset,
    getValues,
    formState: { isValid },
  } = useForm<WeatherDetailsForm>({
    defaultValues: { fromDate: startDateForThisWindow, toDate: new Date() },
  });

  const [submittedFormValues, setSubmittedFormValues] = useState<{
    fromDate: Date;
    toDate: Date;
  }>({
    fromDate: startDateForThisWindow,
    toDate: new Date(),
  });

  const {
    isFetched,
    isLoading,
    data: location,
  } = useLocationHistory({
    locationId: thisWindow?.location_id,
    begin: zonedTimeToUtc(submittedFormValues.fromDate, timezone).toISOString(),
    end: zonedTimeToUtc(submittedFormValues.toDate, timezone).toISOString(),
  });

  const onSubmit = () => {
    const formState = getValues();
    setSubmittedFormValues({ ...formState });
  };

  // Update form after loading window
  useEffect(() => {
    if (!thisWindow?.location_id) return;
    reset({ fromDate: startDateForThisWindow, toDate: new Date() });
  }, [thisWindow?.location_id, reset, startDateForThisWindow]);

  const { formattedForecast: convertedForecast, isLoading: forecastLoading } =
    useFormatForecastData(location?.id);

  // Resize drawer height dynamically
  useEffect(() => {
    const handleResize = () => {
      if (!drawerContainerRef.current) return;
      if (drawerOpen) {
        setDrawerHeight(drawerContainerRef.current.clientHeight);
      } else {
        setDrawerHeight(0);
      }
    };
    handleResize();
    if (window) window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [drawerOpen, isLoading, thisWindow]);

  if ((isLoading && !location) || (forecastLoading && !convertedForecast))
    return (
      <Grid
        container
        style={{
          minHeight: 'calc(93vh - 64px)',
        }}
      >
        <Grid
          style={{
            margin: 0,
            paddingTop: 0,
            minHeight: 'calc(93vh - 64px)',
          }}
          container
          item
          justifyContent='center'
          alignItems='center'
          component={Paper}
        >
          <Grid item>
            <CircularProgress />
          </Grid>
        </Grid>
      </Grid>
    );
  return (
    <Grid
      container
      style={{
        minHeight: 'calc(93vh - 64px)',
      }}
    >
      <Grid
        style={{
          margin: 0,
          paddingTop: 0,
          minHeight: 'calc(93vh - 64px)',
        }}
        container
        item
        direction='column'
        component={Paper}
      >
        <WeatherDetailsFilters
          setValue={setValue}
          onSubmit={onSubmit}
          hide={!!thisWindow?.forecast}
          control={control}
          isValid={isValid}
        />
        {location && thisWindow && (
          <Grid item container xs style={{ position: 'relative' }}>
            {!drawerOpen && <Grid item xs />}
            <Grid item xs={drawerOpen ? 8 : 10}>
              <WeatherChart
                weatherData={
                  thisWindow.forecast
                    ? convertedForecast?.hourlyForecast
                      ? convertedForecast.hourlyForecast
                      : []
                    : location.weather
                }
                measurement={thisWindow.measurement}
                locationName={location.name}
                durationLabel={''}
                spanGaps={!!thisWindow.forecast}
              />
            </Grid>
            <Grid
              item
              style={{ paddingTop: 0, position: 'relative' }}
              ref={drawerContainerRef}
              xs
            >
              <Drawer
                transitionDuration={0}
                anchor='right'
                variant='persistent'
                open={drawerOpen}
                sx={{
                  position: 'relative',
                  marginLeft: 'auto',
                  width: '100%',
                  '& .MuiBackdrop-root': {
                    display: 'none',
                  },
                  '& .MuiDrawer-paper': {
                    width: '100%',
                    position: 'absolute',
                    height: drawerHeight,
                    borderRadius: '10px',
                    border: 0,
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'dark' ? '#2d3640' : '#fff',
                  },
                }}
              >
                <CurrentConditions
                  currentConditions={
                    location.weather[location.weather.length - 1]
                  }
                  location={location}
                  analyzerId={undefined}
                />
              </Drawer>
            </Grid>
            <IconButton
              sx={{
                position: 'absolute',
                right: 10,
                top: -15,
                zIndex: 99999,
              }}
              onClick={() => setDrawerOpen((state) => !state)}
            >
              {drawerOpen ? <ChevronRight /> : <ChevronLeft />}
            </IconButton>
          </Grid>
        )}
      </Grid>
      <InfoPopup
        error
        title='No Data'
        message='No weather data found.'
        openCondition={isFetched && (!location || location.weather.length < 1)}
      />
    </Grid>
  );
};

export default WeatherDetailsView;
